<template>
  <AppModal :show="showConsent" unclosable>
    <h3>{{ $t("cookieConsent.title") }}</h3>
    <div class="mb-2">
      <slot />
    </div>

    <CookieConsentForm v-if="showForm" @submit="agree($event)" />
    <div v-else>
      <AppButton responsive @click="agreeWithAll()">
        {{ $t("cookieConsent.agreeWithAll") }}
      </AppButton>
      <AppButton responsive class="ml-2" variation="inverse" @click="manage()">
        {{ $t("cookieConsent.manage") }}
      </AppButton>
    </div>
  </AppModal>
</template>

<script lang="ts" setup>
import { onMounted, ref } from "vue";
import { getCookie, setCookie } from "../../utils/cookie";
import AppModal from "@/components/app-modal.vue";
import AppButton from "@/components/app-button.vue";
import { CookieConsent } from "../../types/cookie-consent.enum";
import { initializeGoogleAnalytics } from "./google-analytics";
import { initializeMetaPixel } from "./meta-pixel";
import { initializePostHog } from "./posthog-analytics";
import CookieConsentForm from "./cookie-consent-form.vue";

const COOKIE_CONSENT_NAME = "__cookie_consent__";

const allConsentCookieValue = "1";
const allConsents = Object.values(CookieConsent);
const currentValue = ref<CookieConsent[]>([]);
const showConsent = ref(false);
const showForm = ref(false);

const agreeWithAll = () => {
  setCookie(COOKIE_CONSENT_NAME, allConsentCookieValue);
  showConsent.value = false;
  initializeTrackingServices();
};

const agree = (consents: CookieConsent[]) => {
  setCookie(COOKIE_CONSENT_NAME, consents.join(","));
  showConsent.value = false;

  if (consents.includes(CookieConsent.NonEssentials)) {
    initializeTrackingServices();
  }
};

const hasConsent = (consent: CookieConsent) => {
  return currentValue.value.includes(consent);
};

const manage = () => {
  showForm.value = true;
};

const initializeTrackingServices = () => {
  if (window.mangas_gtag_id) {
    initializeGoogleAnalytics(window.mangas_gtag_id);
  }
  if (window.mangas_pixel_id) {
    initializeMetaPixel(window.mangas_pixel_id);
  }

  if (window.mangas_posthog_key) {
    initializePostHog(window.mangas_posthog_key);
  }
};

const getConsentCookieValue = (): CookieConsent[] => {
  const value = getCookie(COOKIE_CONSENT_NAME);
  if (!value) {
    return [];
  }
  if (value === allConsentCookieValue) {
    return allConsents;
  }
  return (value.split(",") as CookieConsent[]).filter((v) =>
    allConsents.includes(v)
  );
};

const handleInit = () => {
  currentValue.value = getConsentCookieValue();
  if (currentValue.value.length === 0 && window.mangasPath !== "privacy") {
    showConsent.value = true;
  }
  if (hasConsent(CookieConsent.NonEssentials)) {
    initializeTrackingServices();
  }
};

const onScrollDown = (handler: () => void) => {
  const listeners: Array<{ event: string; handler: EventListener }> = [];
  let hasTriggered = false;
  let lastScrollY = window.scrollY;

  const removeAllListeners = () => {
    listeners.forEach(({ event, handler }) => {
      window.removeEventListener(event, handler);
    });
    listeners.length = 0;
  };

  // Scroll handler that only triggers on scroll down
  const scrollHandler = () => {
    if (hasTriggered) return;
    const currentScrollY = window.scrollY;
    if (currentScrollY > lastScrollY) {
      // Scrolling down
      hasTriggered = true;
      removeAllListeners();
      handler();
    }
    lastScrollY = Math.max(currentScrollY, 0); // Min to ignore overscroll (negative scrollY) on iOS
  };

  window.addEventListener("scroll", scrollHandler);
  listeners.push({ event: "scroll", handler: scrollHandler });
};

const isEmbedded = () => {
  const isInFacebookWebView = /FBAN|FBAV|IABMV|Instagram/i.test(
    navigator.userAgent
  );
  const isInIframe = window.self !== window.top;

  return isInFacebookWebView || isInIframe;
};

onMounted(() => {
  if (isEmbedded()) {
    // Facebook web view renders bottom nav bar that overlaps modal controls.
    // Wait for any interaction (for dvh units to work properly)
    onScrollDown(() => {
      setTimeout(() => {
        handleInit();
      }, 500);
    });
  } else {
    handleInit();
  }
});
</script>
